import { css, html, LitElement } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { msg } from "@omegagrid/localize";
import { ColorTypes, Dropdown } from "@omegagrid/core";
import { FormCheckbox, FormNumericInput } from "@omegagrid/form";
import { ControlEvent } from "@omegagrid/form/dist/ui/controls/baseControl";
import rp from "../global";
import type { SettingsChangeEvent } from "../container/settings";
import { EDITOR_SETTINGS, EditorSettingSpec, getEditorSettings, isEditorSetting } from "./editorSettings";
import { refreshSqlSchemaCatalog } from "./sqlSchemaCatalog";

// the form controls are referenced so the bundler keeps their registration
void FormCheckbox;
void FormNumericInput;

/**
 * The gear of a code editor's toolbar: the personal editor settings (editorSettings.ts) as checkboxes, buttons and
 * a number input. A change is saved through rp.settings (user scope) at once and every open editor follows it;
 * the SQL settings appear only in editors of the SQL language, together with a button that fetches the database
 * objects of the company again (sqlSchemaCatalog.ts) after the user created some. (Checkboxes rather than
 * og-switch: the switch measures its size when it renders, and the dropdown content renders while it is still
 * closed.)
 */
@customElement('rp-editor-settings-menu')
export class EditorSettingsMenu extends LitElement {

	static styles = css`
		:host {
			display: block;
		}

		div[slot="label"] {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 24px;
		}
	`;

	/** og-dropdown moves the content into its own layer in document.body, so its styles travel with it (as the user menu does) */
	static contentStyle = css`
		.rp-editor-settings {
			min-width: 280px;
			padding: 6px 8px;
			display: flex;
			flex-direction: column;
			gap: 2px;
		}

		.rp-editor-settings .row {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: space-between;
			gap: 12px;
			min-height: 28px;
		}

		.rp-editor-settings .row > span {
			flex: 1;
			white-space: nowrap;
		}

		.rp-editor-settings .choices {
			display: flex;
			flex-direction: row;
			gap: 2px;
		}

		.rp-editor-settings og-form-checkbox {
			height: 24px;
			padding: 0 6px;
		}

		.rp-editor-settings og-form-numericinput {
			width: 80px;
			height: 24px;
		}

		.rp-editor-settings .row > og-button {
			height: 24px;
		}
	`;

	/** language of the editor; the SQL settings are shown for 'sql' only */
	@property({type: String})
	language: string;

	@query('og-dropdown')
	dropdown: Dropdown;

	@state()
	private _refreshing = false;

	private _onSettingsChange = (e: SettingsChangeEvent) => {
		if (isEditorSetting(e.detail.id)) this.requestUpdate();
	};

	connectedCallback() {
		super.connectedCallback();
		rp.settings.addEventListener('change', this._onSettingsChange);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		rp.settings.removeEventListener('change', this._onSettingsChange);
	}

	get items(): EditorSettingSpec[] {
		return EDITOR_SETTINGS.filter(item => !item.sql || this.language === 'sql');
	}

	private set(id: string, value: unknown) {
		rp.settings.set(id, value);
	}

	/** the refresh of the database objects belongs to the SQL editors with the objects switched on */
	get hasSchemaRefresh(): boolean {
		return this.language === 'sql' && getEditorSettings().schema;
	}

	private async refreshSchema() {
		if (this._refreshing) return;
		this._refreshing = true;
		try {
			const loaded = await refreshSqlSchemaCatalog();
			if (!loaded) {
				rp.host.showAlert({
					type: 'warning',
					title: msg('Editor settings'),
					content: msg('The database objects could not be loaded.'),
				});
			}
		} finally {
			this._refreshing = false;
		}
	}

	private renderControl(item: EditorSettingSpec) {
		const value = rp.settings.get(item.id);
		switch (item.type) {
			case 'n':
				return html`
					<og-form-numericinput
						.value="${typeof value === 'number' ? value : null}"
						@change="${(e: ControlEvent<number>) => this.set(item.id, Number.isFinite(e.value) ? e.value : null)}">
					</og-form-numericinput>
				`;
			case 's': {
				const options = (typeof item.content === 'function' ? item.content() : item.content) as {k: string, v: string}[];
				return html`
					<div class="choices">
						${options.map(option => html`
							<og-button
								color="${option.k === value ? ColorTypes.Accent : ColorTypes.Gray}"
								@click="${() => this.set(item.id, option.k)}">
								<div slot="content">${msg(option.v)}</div>
							</og-button>
						`)}
					</div>
				`;
			}
			default:
				return html`
					<og-form-checkbox
						.value="${value !== false && value !== 0 && value !== '0'}"
						@change="${(e: ControlEvent<boolean>) => this.set(item.id, !!e.value)}">
					</og-form-checkbox>
				`;
		}
	}

	render = () => html`
		<og-dropdown color="transparent" alignment="right" noCaret title="${msg('Editor settings')}">
			<div slot="label">
				<og-icon icon="gear"></og-icon>
			</div>
			<div slot="content" class="rp-editor-settings">
				<style>${EditorSettingsMenu.contentStyle}</style>
				${this.items.map(item => html`
					<div class="row">
						<span>${item.label()}</span>
						${this.renderControl(item)}
					</div>
				`)}
				${this.hasSchemaRefresh ? html`
					<div class="row">
						<span>${msg('Refresh database objects')}</span>
						<og-button
							icon="${this._refreshing ? 'spinner' : 'arrows-rotate'}"
							color="${ColorTypes.Gray}"
							?disabled="${this._refreshing}"
							@click="${() => this.refreshSchema()}">
						</og-button>
					</div>
				` : ''}
			</div>
		</og-dropdown>
	`;

}
